import { useEffect, useState, type ReactElement } from 'react';
import { useSession } from '../../auth';
import { trackFailed } from '../../../../../shared-web/track';
import { emberApi, type HomeResponse, type HomeTail, type PlaceResponse } from '../api';
import { cachedHome, loadHome } from '../home-page-cache';
import { Link } from '../components/Link';
import { InboundRegistrationCTA } from '../components/InboundRegistrationCTA';
import { JoinEmberCloser } from '../components/JoinEmberCloser';
import { ProfileGlyph } from '../components/ProfileGlyph';
import { LightAsk } from '../tree/LightAsk';
import { HeroComposer } from '../tree/HeroComposer';
import { NavAccountButton } from '../components/NavAccountButton';
import { PageBack } from '../components/PageBack';
import { PageHome } from '../components/PageHome';
import { TreeSearch } from '../tree/TreeSearch';
import { navigate } from '../router';
import { useTalkSheet } from '../talk-sheet';
import { PageFailure } from '../components/PageFailure';
import { PageLoading } from '../components/PageLoading';
import { PeopleLine } from '../tree/PeopleLine';
import { quotedOpening, searchedOpening } from '../tree/searched-opening';
import { lampAlpha } from '../tree/lights';
import { fullDate } from '../format';
import './PlacePage.css';

/**
 * S16 — a light's own page, /places/:slug (v4.0 §4.17). Server-rendered for
 * crawlers and the first paint. Nothing of anyone's Ember is on it.
 *
 * One order since 2026-09-21 (三次拍板), by where the words come from:
 *
 *   ground   the light — kicker, title, count; then the people who stood
 *            here: the question box with its offered questions and the open
 *            answer (LightAsk, on lights enough people stood at; under that,
 *            one line says when the box appears); then the door.
 *   paper    the place — the situation section an editor published
 *            (life-node-seo.js); then what happened here, week by week; then
 *            the lights nearby; then one fixed FAQ on how the page works,
 *            and the footer.
 *
 * The door is the homepage's composer (2026-09-24 拍板): the first sentence
 * typed here is the first turn, the arrow and the handset work with or
 * without one, and the empty field raises the list of lights — this light's
 * first line first, then the homepage's hottest lights in the homepage's
 * order. Its words are the homepage's own (the home read: the placeholders
 * and the trust line; GET /api/tree/you: the thread's tail), so the two
 * doors cannot drift apart. It replaced the cream pill on the ground and the
 * answer's door inside the question box. The page has this one door: the
 * closing dusk band under the paper (`You don't need to have the right
 * words.`, the editor's closing line, the door once more; 2026-09-17 to
 * 2026-09-25) retired with its closing_line field (migration 290).
 *
 * Reached from a Google ad (2026-10-04 拍板, Google only, after the 9/30–10/3
 * probe: 22 people read the first screen, 14 left without a touch), the door
 * speaks in the words they searched — `utm_term`, the keyword the search
 * matched, read back by searched-opening.ts: the heading quotes it in place
 * of `Going through this yourself?`, the line says to finish it here, and
 * the sentence waits in the field with the arrow and the handset as they
 * are. Only for a browser with no open conversation (the tail's slot is the
 * thread's); any other arrival, or a term that is not a sentence, sees the
 * page as it was. No new fact: the arrival's page.viewed carries the term.
 *
 * Two Join Ember doors for visitors (2026-09-24 拍板; mock brain
 * mocks/light-page-join-doors-2026-09-24.html): the homepage's nav pill in
 * the toolbar's right cluster (Back · Home · Join Ember), and its contact
 * card on the paper, after the place's own words and before Nearby — the end
 * of what this place has to say and the start of the ways off the page
 * (prod 09-21..24: 93 of 96 readers were not registered; Week by week was
 * reached by 67% of them, Nearby by 65%, the closing band by 47%). A
 * signed-in person sees neither; nothing else on the page moves.
 *
 * Drawn as one object per part (2026-09-21 UI 拍板, mock
 * light-page-ui-2026-09-21): the title at the homepage's scale, the question
 * box as one card on the dusk with its answer inside it, the paper without
 * glowing bullets or card shadows, the weeks as a log, the nearby lights on
 * one straight branch.
 *
 * What stood on the page before and left that day: the canopy band above the
 * title (2026-09-07; Nearby already lists the same lights), the composer on
 * the ground (2026-09-17; one telling door again), the definition as two
 * cards (2026-09-08; the situation section says what the place is, in the
 * editor's words), How Ember works and the per-place FAQ (2026-09-17; the
 * page explains itself once, the same on every light), the closing dusk band
 * (2026-09-25; one door on the page).
 */
export function PlacePage({ slug, initialData = null }: {
  slug: string;
  initialData?: PlaceResponse | null;
}): ReactElement {
  const { status: session } = useSession();
  const openTalk = useTalkSheet();
  const [data, setData] = useState<PlaceResponse | null>(initialData?.node.slug === slug ? initialData : null);
  const [failed, setFailed] = useState<false | 'gone' | 'load_failed'>(false);
  // The composer's words and lights are the homepage's (one shared read per
  // document), and its tail is this browser's own conversation.
  const [home, setHome] = useState<HomeResponse | null>(() => cachedHome());
  const [tail, setTail] = useState<HomeTail | null>(null);
  const thread = Boolean(tail?.rows.length);
  // Whether this browser has an open conversation is known once /api/tree/you
  // has answered either way; the searched words wait for that answer.
  const [youKnown, setYouKnown] = useState(false);
  // The words searched before landing here (Google only), read on the client
  // after the first paint: the server-rendered page is the same for everyone.
  const [searched, setSearched] = useState('');

  useEffect(() => {
    let alive = true;
    setSearched(searchedOpening(window.location.search));
    loadHome(() => emberApi.home())
      .then((next) => { if (alive) setHome(next); })
      .catch(() => { if (alive) trackFailed('place_load', 'home_failed'); });
    emberApi.treeYou()
      .then((r) => { if (alive) setTail(r.tail ?? null); })
      .catch(() => { if (alive) trackFailed('place_load', 'tree_you_failed'); })
      .finally(() => { if (alive) setYouKnown(true); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    if (initialData?.node.slug === slug) { setData(initialData); return () => { alive = false; }; }
    setData(null);
    setFailed(false);
    emberApi.place(slug)
      .then((d) => { if (alive) setData(d); })
      .catch((err: unknown) => {
        if (!alive) return;
        const status = (err as { status?: number }).status;
        setFailed(status === 404 ? 'gone' : 'load_failed');
        trackFailed('place_load', status === 404 ? 'not_found' : 'load_failed');
      });
    return () => { alive = false; };
  }, [slug, initialData]);

  if (failed === 'gone') {
    return <PageFailure title="This place isn’t on the tree" detail="The link may be old, or the light has not appeared yet." logState="place_gone" />;
  }
  if (failed) {
    return <PageFailure title="Ember is briefly unreachable" detail="Refresh to try again." logState="place_failed" />;
  }
  if (!data) return <PageLoading />;

  const { node, neighbours, ask = null, weeks = [], seo = null } = data;
  const eligible = Boolean(ask?.eligible);
  // The searched words open the door only for a stranger to Ember: with a
  // thread the tail stands in that slot and the field is the next turn.
  const opening = youKnown && !thread ? searched : '';
  const composer = (
    <HeroComposer
      placeholder={home?.hero.composer_placeholder ?? ''}
      returnPlaceholder={home?.hero.composer_placeholder_return ?? ''}
      tail={tail}
      lights={home?.tree?.nodes ?? []}
      here={node}
      request={{ entryType: 'node', entryId: node.id, returnTo: `/places/${encodeURIComponent(node.slug)}` }}
      discovery="place"
      opening={opening}
    />
  );

  return (
    <div className="place-page">
      <div className="place-ground">
        {/* The same Back / Home row as every other non-home page (2026-09-06
            走查), on the dusk. A cold arrival from search has nothing behind
            it, so Back lands on the tree. */}
        <div className="place-toolbar place-col" data-eh-top-bar>
          <PageBack className="place-back" />
          <div className="ember-toolbar-actions place-toolbar-actions">
            <PageHome />
            {/* The homepage's search, docked here as it is over the tree
                (2026-10-01 拍板: the light's page alone takes the search and
                the avatar back; the other Back / Home pages stay). A pick
                walks to that light's page; None of these opens the telling
                from this light. */}
            <TreeSearch
              onPick={(picked) => navigate(`/places/${encodeURIComponent(picked.slug)}`)}
              onTell={() => openTalk({ entryType: 'node', entryId: node.id, returnTo: `/places/${encodeURIComponent(node.slug)}` })}
            />
            {/* Signed in, the homepage's row — Chats, the notebook, the
                person's own animal and its menu; visitors, the Join Ember pill,
                glass on the dusk as it is over the tree; nothing while the
                session is still loading (a pill that vanishes a beat later
                reads worse than a beat of blank — the landing nav's rule). */}
            {session === 'loading' ? null : session === 'authenticated' ? (
              <NavAccountButton />
            ) : (
              <InboundRegistrationCTA
                className="place-join-pill"
                icon={<ProfileGlyph />}
                label="Join Ember"
                returnTo={`/places/${encodeURIComponent(node.slug)}`}
                logName="place_register_open"
                discovery="nav"
              />
            )}
          </div>
        </div>
        <header className="place-head place-col">
          {/* The head is the light's name and how many stood here; the lit
              point that stood over the name (what remained of the sky's
              canopy after 2026-09-21) and the kicker `A place on the tree`
              both left the same day (拍板): the amber count under the title
              is the light, said. */}
          <h1 className="place-title">{node.title}</h1>
          <p className="place-people"><PeopleLine count={node.people_count} /></p>
          {/* Ask this light (§4.17, 2026-09-21 拍板): on a light enough people
              stood at, the question box stands right under the count. Under
              that, the page says once when the box appears (the threshold is
              LIGHT_ASK.MIN_PEOPLE, light-ask.js); the FAQ below opens on the
              same question. */}
          {ask ? (
            eligible
              ? <LightAsk key={node.id} slug={node.slug} nodeId={node.id} ask={ask} />
              : <p className="place-soon">Once a few more people have stood here, you can ask what they went through.</p>
          ) : null}
          {/* The ground door (2026-09-21 四次拍板; the composer since
              2026-09-24) on every light, under the question box where there
              is one: whoever has just read an answer meets the field next.
              From 2026-09-22 to 09-24 a light that takes questions carried
              its door inside the answer instead; a second field inside the
              box, looking like the box's own and meaning something else, is
              why it came back out. The trust line is the homepage's. */}
          <div className="place-tell">
            <span className="place-tell-rule" aria-hidden />
            {opening ? (
              <>
                <h2 className="place-tell-q">&ldquo;{quotedOpening(opening)}&rdquo;</h2>
                <p className="place-tell-line">Say the rest of it here. Ember thinks it through with you.</p>
              </>
            ) : (
              <>
                <h2 className="place-tell-q">Going through this yourself?</h2>
                <p className="place-tell-line">Ember thinks it through with you, knowing how it went for people who stood here.</p>
              </>
            )}
            {composer}
            {home ? (
              <p className="tree-home-trust place-trust">
                {home.hero.trust_line}
                {' '}
                <a href="/privacy-and-sharing" className="tree-home-trust-link" data-log="landing_privacy_open">Privacy →</a>
              </p>
            ) : null}
          </div>
        </header>
      </div>
      <div className="place-paper place-col">
        {seo ? (
          /* The situation section (SEO layer, 2026-09-17 拍板, life-node-seo.js):
             the place in an editor's words, for the person who searched this
             situation — first on the paper, where the definition cards stood
             until 2026-09-21. `seo` is null until an editor publishes it, or
             `published:false` on the token-gated preview. */
          <section className="place-guide" aria-labelledby="place-guide-title" data-log-seen="place_guide" data-log-node-id={node.id}>
            {!seo.published ? <p className="place-guide-draft">Draft preview — not published</p> : null}
            <h2 className="place-h2" id="place-guide-title">{seo.situation.heading}</h2>
            {seo.situation.intro ? <p className="place-guide-intro">{seo.situation.intro}</p> : null}
            {seo.situation.cards.length > 0 ? (
              <div className={`place-shape-cards${seo.situation.cards.length > 1 ? ' is-two' : ''}`}>
                {seo.situation.cards.map((card) => (
                  <div className="place-shape-card" key={card.title}>
                    <h3 className="place-card-title">{card.title}</h3>
                    <p>{card.body}</p>
                  </div>
                ))}
              </div>
            ) : null}
          </section>
        ) : null}
        {weeks.length > 0 ? (
          /* What happened here, week by week (§4.17, 2026-09-21): one entry
             per PT week in which at least five new Embers stood here, newest
             first, drawn from that week's people together. A week with too
             few new arrivals has no entry; the last line says so. */
          <section className="place-weeks" aria-labelledby="place-weeks-title" data-log-seen="place_weeks" data-log-node-id={node.id}>
            <h2 className="place-h2" id="place-weeks-title">What happened here, week by week</h2>
            <ul className="place-weeks-list">
              {weeks.map((w) => (
                <li className="place-week" key={w.week_start}>
                  <p className="place-week-meta">
                    <b>Week of {monthDay(w.week_start)}</b>
                    <span>· {w.new_embers} new {w.new_embers === 1 ? 'Ember' : 'Embers'} from {w.new_people} {w.new_people === 1 ? 'person' : 'people'}</span>
                    <span>· Written {fullDate(w.written_at)}</span>
                  </p>
                  <p>{w.text}</p>
                </li>
              ))}
            </ul>
            <p className="place-shape-from">A week gets an entry when at least five new Embers arrive. Drawn from everyone who arrived that week, never from any one of them.</p>
          </section>
        ) : null}
        {/* The contact card (visitors only; JoinEmberCloser renders nothing
            otherwise): its own section slot in the paper's rhythm, no heading
            — the card is the object. Its own column is the card's container,
            so it stacks here at every width. */}
        {session === 'anonymous' ? (
          <section className="place-join" aria-label="Join Ember">
            <JoinEmberCloser returnTo={`/places/${encodeURIComponent(node.slug)}`} logName="place_register_open" discovery="card" />
          </section>
        ) : null}
        {neighbours.length > 0 ? (
          <section className="place-near" aria-labelledby="place-near-title" data-log-seen="place_near" data-log-node-id={node.id}>
            <h2 className="place-h2" id="place-near-title">Nearby on the tree</h2>
            {/* One straight branch down the list (the list's own left edge);
                each row's dot is a light hanging on it, dimmer or brighter
                for how many stood there. */}
            <ul className="place-near-list">
              {neighbours.map((n) => (
                <li key={n.id}>
                  <Link to={`/places/${encodeURIComponent(n.slug)}`} className="place-near-link" data-log="tree_light_open" data-log-node-id={n.id} data-log-state="neighbour">
                    <span className="place-near-lamp" aria-hidden><span className="place-light" style={{ opacity: lampAlpha(n.people_count) }} /></span>
                    <span className="place-near-title">{n.title}</span>
                    <small aria-label={`${n.people_count} ${n.people_count === 1 ? 'person has' : 'people have'} stood here`}>
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="8" r="4" /><path d="M5 21a7 7 0 0 1 14 0" /></svg>
                      {n.people_count.toLocaleString('en-US')}
                    </small>
                  </Link>
                </li>
              ))}
            </ul>
            <p className="place-note">Only the place shows here. What people told stays theirs.</p>
          </section>
        ) : null}
        {/* How this page works (2026-09-21 三次拍板): one fixed set of
            questions for every light, about the page itself — what can be
            asked, where answers come from, what "stood here" means, what
            telling gives you — and, on a light too small for the box, why
            there is nothing to read yet. Last on the paper: the hesitation
            before telling is answered where it is felt. The editor's
            per-place questions that stood here from 2026-09-17 retired with
            this. */}
        <section className="place-faq" aria-labelledby="place-faq-title" data-log-seen="place_faq" data-log-node-id={node.id}>
          <h2 className="place-h2" id="place-faq-title">How this page works</h2>
          <div className="place-faq-list">
            {(eligible ? FAQ_ELIGIBLE : FAQ_SMALL).map((item, i) => (
              <details key={item.q} open={i === 0}>
                <summary>{item.q}<span aria-hidden>+</span></summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

/** A PT week's Monday (YYYY-MM-DD) as "September 14". */
function monthDay(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (!Number.isFinite(d.getTime())) return isoDate;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
}

/**
 * The page's own questions. Two sets, by whether the light takes questions
 * (LIGHT_ASK.MIN_PEOPLE distinct people with accounts, said here as "five");
 * the last two are the same in both.
 */
const FAQ_SHARED = [
  { q: 'What does “stood here” mean?', a: 'Each of them told Ember something that happened to them, and their page was placed on this light. The pages stay theirs. What is public is the count and, once enough people stand here, answers drawn from what at least two of them share — never anyone’s words.' },
  { q: 'What happens if I tell my part?', a: 'Once you’ve said enough, Ember writes one clear page of what happened, in your words, yours to keep. If what you told could help someone standing here later, Ember asks you once — no one can read your Ember unless you separately say yes.' },
];
const FAQ_ELIGIBLE = [
  { q: 'What can I ask here?', a: 'What the people who stood here went through: what they tried, what changed, what came after. Type your own question, or take one that someone here asked before you.' },
  { q: 'Where do the answers come from?', a: 'From the people who stood here. Each of them told Ember what happened to them; Ember reads all of it together and answers only with what at least two of them share. No sentence is one person’s story.' },
  { q: 'Why didn’t my question get an answer?', a: 'Nobody here has been through that part yet, or only one person has. The answer grows as more people stand here.' },
  ...FAQ_SHARED,
];
const FAQ_SMALL = [
  { q: 'Why is there nothing to read here?', a: 'Ember answers questions on a light only once at least five people whose experience it can draw on have stood there, so no answer can be one person’s story. Not yet, here.' },
  { q: 'What appears once there are five?', a: 'A place to ask what they went through: what they tried, what changed, what came after. Ember reads all of their pages together and answers only with what at least two of them share.' },
  ...FAQ_SHARED,
];
